import { addSeconds, isBefore } from 'date-fns';
import { SESSION_TYPES } from '@medflex/utils/constants';
import sortBy from 'lodash/sortBy';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import config from '@medflex/utils/config';
import useUserProfile from './useUserProfile';

const { SESSIONS_URL } = config;

const isCaller = ({ participants }, userId) =>
  participants.some(
    participant =>
      participant.userId === userId && participant.roles && participant.roles.includes('moderator'),
  );

const useSessionCalls = () => {
  const { sessions } = useSelector(state => state.sessions);
  const dispatch = useDispatch();
  const { userProfile } = useUserProfile();
  const [outgoing, setOutgoing] = useState(null);
  const [createState, setCreateState] = useState('idle');

  const calls = useMemo(
    () =>
      sortBy(
        sessions.filter(
          ({ type, participants }) =>
            type === SESSION_TYPES.CALL &&
            participants.some(participant => participant.userId === userProfile.id),
        ),
        s => s.createdAt,
      ),
    [sessions, userProfile],
  );

  const incoming = useMemo(() => {
    const lastCall = calls.slice(-1)[0];
    if (
      lastCall &&
      !lastCall.endedAt &&
      isBefore(new Date(), addSeconds(new Date(lastCall.createdAt), 30))
    ) {
      if (!isCaller(lastCall, userProfile.id)) {
        return lastCall;
      }
    }
    return null;
  }, [calls, userProfile]);

  const getUrl = ({ id, settings = 0 }) => {
    const call = calls.find(c => c.id === id);
    if (call) {
      const { tan, participantId } = call.participants.find(p => p.userId === userProfile.id);
      return `${SESSIONS_URL}/${id}/${participantId}?tan=${tan}&settings=${settings}`;
    }
    return null;
  };

  const goTo = (...params) => {
    const url = getUrl(...params);
    if (url) window.open(url, '_blank');
  };

  const create = async participants => {
    const attributes = {
      participants,
      type: SESSION_TYPES.CALL,
    };

    setCreateState('loading');
    try {
      await dispatch.sessions.create(attributes);
      setCreateState('succeeded');
    } catch (e) {
      setCreateState('failed');
      throw e;
    }
  };

  const cancel = ({ id }) => {
    const call = calls.find(c => c.id === id);
    if (call) {
      const { tan, participantId } = call.participants.find(p => p.userId === userProfile.id);
      dispatch.sessions.update({
        id,
        participantId,
        tan,
        attributes: { endedAt: new Date().toISOString() },
      });
    }
  };

  useEffect(() => {
    if (createState === 'succeeded' && calls) {
      const lastCall = calls.slice(-1)[0];
      if (isCaller(lastCall, userProfile.id)) {
        setOutgoing(calls.slice(-1)[0]);
      }
      setCreateState('idle');
    }
  }, [createState, calls, userProfile]);

  const load = () => dispatch.sessions.load();

  return {
    create,
    cancel,
    load,
    calls,
    incoming,
    outgoing,
    getUrl,
    goTo,
  };
};

export default useSessionCalls;
